/**
 * Column Detail Dialog Component
 * Displays detailed information about a data element (column) in Unidatum-style layout
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Breadcrumbs,
  Link,
  Tabs,
  Tab,
  Grid,
  Chip,
  Paper,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  DialogTitle,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditIcon from '@mui/icons-material/Edit';
import TableChartIcon from '@mui/icons-material/TableChart';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import CategoryIcon from '@mui/icons-material/Category';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import RefreshIcon from '@mui/icons-material/Refresh';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import ListIcon from '@mui/icons-material/List';
import CodeIcon from '@mui/icons-material/Code';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Tooltip from '@mui/material/Tooltip';
import MongoDBIcon from '../icons/MongoDBIcon';
import { DataElement } from '../../types/DataAsset';
import { columnService } from '../../services/api';
import { Alert, Snackbar } from '@mui/material';
import ColumnStatistics from './ColumnStatistics';
import { getColumnStatistics, ColumnStatistics as ColumnStatsType } from '../../services/columnStatisticsService';
import ColumnDiagram from './ColumnDiagram';
import ColumnTechnicalLineage from './ColumnTechnicalLineage';
import ColumnQuality from './ColumnQuality';
import ColumnResponsibilities from './ColumnResponsibilities';
import ColumnHistory from './ColumnHistory';

interface ColumnDetailDialogProps {
  open: boolean;
  onClose: () => void;
  element: DataElement | null;
  tableName?: string;
  domain?: string;
  assetId?: string;
  onUpdate?: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`column-tabpanel-${index}`}
      aria-labelledby={`column-tab-${index}`}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const ColumnDetailDialog: React.FC<ColumnDetailDialogProps> = ({
  open,
  onClose,
  element,
  tableName,
  domain,
  assetId,
  onUpdate
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [leftSection, setLeftSection] = useState('overview');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    description: '',
    dataType: '',
    isNullable: false
  });
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [statistics, setStatistics] = useState<ColumnStatsType | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [attributeSearch, setAttributeSearch] = useState('');
  const [viewMode, setViewMode] = useState<'graph' | 'list'>('graph');
  const [showCode, setShowCode] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [addAttributeOpen, setAddAttributeOpen] = useState(false);

  // Load statistics when element changes
  useEffect(() => {
    if (element && open) {
      loadStatistics();
    }
  }, [element, open]);

  const loadStatistics = async () => {
    if (!element) return;
    
    setLoadingStats(true);
    try {
      const stats = await getColumnStatistics(
        element.name,
        element.dataType || 'VARCHAR',
        tableName || 'UNKNOWN'
      );
      setStatistics(stats);
    } catch (error) {
      console.error('Error loading statistics:', error);
      setStatistics(null);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleEditClick = () => {
    if (element) {
      setEditForm({
        description: element.description || '',
        dataType: element.dataType || '',
        isNullable: element.isNullable || false
      });
      setEditDialogOpen(true);
    }
  };

  const handleEditSave = async () => {
    if (!assetId || !element) {
      setNotification({
        open: true,
        message: 'Missing asset ID or element information',
        severity: 'error'
      });
      return;
    }

    setSaving(true);
    try {
      await columnService.updateColumn(assetId, element.name, editForm);
      
      setNotification({
        open: true,
        message: 'Column updated successfully!',
        severity: 'success'
      });
      
      setEditDialogOpen(false);
      
      // Call the onUpdate callback to refresh the parent data
      if (onUpdate) {
        onUpdate();
      }
    } catch (error: any) {
      setNotification({
        open: true,
        message: error.message || 'Failed to update column',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEditCancel = () => {
    setEditDialogOpen(false);
  };

  // Toolbar action handlers
  const handleAddAttribute = () => {
    setAddAttributeOpen(true);
    setNotification({
      open: true,
      message: 'Add Attribute feature - Coming soon!',
      severity: 'info'
    });
  };

  const handleRemoveAttribute = () => {
    if (!attributeSearch) {
      setNotification({
        open: true,
        message: 'Please search for an attribute first',
        severity: 'warning'
      });
      return;
    }
    setNotification({
      open: true,
      message: `Remove attribute "${attributeSearch}" - Feature coming soon!`,
      severity: 'info'
    });
  };

  const handleRefresh = async () => {
    setNotification({
      open: true,
      message: 'Refreshing column data...',
      severity: 'info'
    });
    await loadStatistics();
    if (onUpdate) {
      onUpdate();
    }
    setNotification({
      open: true,
      message: 'Column data refreshed successfully!',
      severity: 'success'
    });
  };

  const handleFitScreen = () => {
    setNotification({
      open: true,
      message: 'Fit to screen applied',
      severity: 'success'
    });
    // This will be used by child components (ReactFlow)
    window.dispatchEvent(new CustomEvent('fitView'));
  };

  const handleToggleView = () => {
    const newMode = viewMode === 'graph' ? 'list' : 'graph';
    setViewMode(newMode);
    setNotification({
      open: true,
      message: `Switched to ${newMode} view`,
      severity: 'success'
    });
  };

  const handleToggleCode = () => {
    setShowCode(!showCode);
    setNotification({
      open: true,
      message: showCode ? 'Code view hidden' : 'Code view shown',
      severity: 'info'
    });
  };

  const handleHelp = () => {
    setHelpOpen(!helpOpen);
    setNotification({
      open: true,
      message: 'Help documentation opened',
      severity: 'info'
    });
  };

  if (!element) return null;

  const getAssetTypeIcon = () => {
    return '📊';
  };

  const getAssetTypeColor = (type: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    const colorMap: { [key: string]: "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" } = {
      'Column': 'primary',
      'Attribute': 'secondary',
      'Field': 'info'
    };
    return colorMap[type] || 'default';
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh'
        }
      }}
    >
      {/* Header with breadcrumbs */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box>
          <Breadcrumbs
            separator="›"
            sx={{
              color: 'white',
              mb: 1,
              '& .MuiBreadcrumbs-separator': {
                color: 'white'
              }
            }}
          >
            <Link
              color="inherit"
              component="button"
              onClick={(e) => { 
                e.preventDefault();
                onClose();
                navigate('/data-catalog');
              }}
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                textDecoration: 'none',
                border: 'none',
                background: 'none',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              <MongoDBIcon sx={{ fontSize: 18 }} />
              E-UNIFY
            </Link>
            {domain && (
              <Link
                color="inherit"
                component="button"
                onClick={(e) => { 
                  e.preventDefault();
                  onClose();
                  navigate('/data-catalog');
                }}
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  textDecoration: 'none',
                  border: 'none',
                  background: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                <CategoryIcon sx={{ fontSize: 18 }} />
                {domain}
              </Link>
            )}
            {tableName && assetId && (
              <Link
                color="inherit"
                component="button"
                onClick={(e) => { 
                  e.preventDefault();
                  onClose();
                  navigate(`/data-assets/${assetId}`);
                }}
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                <TableChartIcon sx={{ fontSize: 18 }} />
                {tableName}
              </Link>
            )}
            <Typography 
              color="inherit"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              <ViewColumnIcon sx={{ fontSize: 18 }} />
              {element.name}
            </Typography>
          </Breadcrumbs>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span style={{ fontSize: '24px' }}>{getAssetTypeIcon()}</span>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 600, 
                fontFamily: 'Source Sans Pro, sans-serif', 
                color: 'white',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              {element.name}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, color: 'white' }}>
            Column <InfoOutlinedIcon sx={{ fontSize: 14, color: 'white' }} />
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            onClick={handleEditClick}
            sx={{ 
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
            aria-label="Edit column"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            onClick={onClose}
            sx={{ 
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
            aria-label="Close dialog"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f5f5f5' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              minHeight: 48,
              color: '#666',
              '&.Mui-selected': {
                color: 'primary.main'
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: 'primary.main',
              height: 3
            }
          }}
        >
          <Tab label="Summary" />
          <Tab label="Diagram" />
          <Tab label="Technical Lineage" />
          <Tab label="Quality" />
          <Tab label="Responsibilities" />
          <Tab label="History" />
        </Tabs>
      </Box>

      {/* Action Toolbar */}
      <Box sx={{ 
        borderBottom: 1, 
        borderColor: 'divider', 
        bgcolor: 'white',
        px: 2,
        py: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <TextField
          size="small"
          placeholder="Attributes"
          variant="outlined"
          value={attributeSearch}
          onChange={(e) => setAttributeSearch(e.target.value)}
          sx={{ 
            width: 240,
            '& .MuiOutlinedInput-root': {
              bgcolor: 'white'
            }
          }}
        />
        
        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
        
        <Tooltip title="Add new attribute" arrow>
          <IconButton 
            size="small" 
            sx={{ color: '#1976d2' }}
            onClick={handleAddAttribute}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Remove attribute" arrow>
          <IconButton 
            size="small"
            onClick={handleRemoveAttribute}
          >
            <RemoveIcon />
          </IconButton>
        </Tooltip>
        
        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
        
        <Tooltip title="Refresh data" arrow>
          <IconButton 
            size="small"
            onClick={handleRefresh}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Fit to screen" arrow>
          <IconButton 
            size="small"
            onClick={handleFitScreen}
          >
            <FitScreenIcon />
          </IconButton>
        </Tooltip>
        
        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
        
        <Tooltip title={viewMode === 'graph' ? 'Switch to list view' : 'Switch to graph view'} arrow>
          <IconButton 
            size="small"
            onClick={handleToggleView}
            sx={{ color: viewMode === 'list' ? '#1976d2' : 'inherit' }}
          >
            <ListIcon />
          </IconButton>
        </Tooltip>
        
        <Tooltip title={showCode ? 'Hide source code' : 'Show source code'} arrow>
          <IconButton 
            size="small"
            onClick={handleToggleCode}
            sx={{ color: showCode ? '#1976d2' : 'inherit' }}
          >
            <CodeIcon />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Help" arrow>
          <IconButton 
            size="small"
            onClick={handleHelp}
            sx={{ color: helpOpen ? '#1976d2' : 'inherit' }}
          >
            <HelpOutlineIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <DialogContent sx={{ p: 0, display: 'flex', height: 'calc(100% - 240px)' }}>
        {/* Left Sidebar */}
        <Box
          sx={{
            width: 200,
            borderRight: 1,
            borderColor: 'divider',
            bgcolor: '#fafafa',
            p: 2
          }}
        >
          <List dense>
            <ListItemButton
              selected={leftSection === 'overview'}
              onClick={() => setLeftSection('overview')}
              sx={{
                borderRadius: 1,
                mb: 0.5,
                '&.Mui-selected': {
                  bgcolor: '#e3f2fd',
                  color: 'primary.main',
                  fontWeight: 600
                }
              }}
            >
              <ListItemText primary="Overview" />
            </ListItemButton>
            <ListItemButton
              selected={leftSection === 'statistics'}
              onClick={() => setLeftSection('statistics')}
              sx={{
                borderRadius: 1,
                mb: 0.5,
                '&.Mui-selected': {
                  bgcolor: '#e3f2fd',
                  color: 'primary.main',
                  fontWeight: 600
                }
              }}
            >
              <ListItemText primary="Descriptive Statistics" />
            </ListItemButton>
            <ListItemButton
              selected={leftSection === 'details'}
              onClick={() => setLeftSection('details')}
              sx={{
                borderRadius: 1,
                mb: 0.5,
                '&.Mui-selected': {
                  bgcolor: '#e3f2fd',
                  color: 'primary.main',
                  fontWeight: 600
                }
              }}
            >
              <ListItemText primary="Details" />
            </ListItemButton>
            <ListItemButton
              selected={leftSection === 'comments'}
              onClick={() => setLeftSection('comments')}
              sx={{
                borderRadius: 1,
                '&.Mui-selected': {
                  bgcolor: '#e3f2fd',
                  color: 'primary.main',
                  fontWeight: 600
                }
              }}
            >
              <ListItemText primary="Comments" />
            </ListItemButton>
          </List>
        </Box>

        {/* Main Content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <TabPanel value={activeTab} index={0}>
            <Box sx={{ px: 4 }}>
              {/* Breadcrumb trail - Unidatum Style */}
              <Breadcrumbs 
                separator="›" 
                sx={{ 
                  mb: 2, 
                  fontSize: '14px',
                  '& .MuiBreadcrumbs-separator': {
                    color: '#71767A'
                  }
                }}
              >
                <Chip
                  icon={<MongoDBIcon sx={{ fontSize: 16 }} />}
                  label="E-UNIFY"
                  size="small"
                  onClick={() => {
                    onClose();
                    navigate('/data-catalog');
                  }}
                  sx={{ 
                    bgcolor: 'divider', 
                    cursor: 'pointer',
                    fontWeight: 500,
                    '&:hover': {
                      bgcolor: '#d0d0d0'
                    }
                  }}
                />
                <Chip
                  icon={<CategoryIcon sx={{ fontSize: 16 }} />}
                  label={(domain || 'FINANCIALS').toUpperCase().replace(/ /g, '_')}
                  size="small"
                  onClick={() => {
                    onClose();
                    navigate('/data-catalog');
                  }}
                  sx={{ 
                    bgcolor: 'divider', 
                    cursor: 'pointer',
                    fontWeight: 500,
                    '&:hover': {
                      bgcolor: '#d0d0d0'
                    }
                  }}
                />
                <Chip
                  icon={<TableChartIcon sx={{ fontSize: 16 }} />}
                  label={(tableName || 'CUSTOMER_DATA').toUpperCase().replace(/ /g, '_')}
                  size="small"
                  onClick={() => {
                    if (assetId) {
                      onClose();
                      navigate(`/data-assets/${assetId}`);
                    }
                  }}
                  sx={{ 
                    bgcolor: 'divider', 
                    cursor: 'pointer',
                    fontWeight: 500,
                    '&:hover': {
                      bgcolor: '#d0d0d0'
                    }
                  }}
                />
                <Chip
                  icon={<ViewColumnIcon sx={{ fontSize: 16 }} />}
                  label={element.name.toUpperCase().replace(/ /g, '_')}
                  size="small"
                  sx={{ 
                    bgcolor: 'divider',
                    fontWeight: 500,
                    cursor: 'default'
                  }}
                />
              </Breadcrumbs>

              {leftSection === 'overview' && (
                <>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Overview
                  </Typography>

                  {/* Description */}
                  <Paper sx={{ p: 2, mb: 3, bgcolor: '#f9f9f9' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Description
                      </Typography>
                      <Chip label="From This Asset" size="small" variant="outlined" />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {element.description || 'No description available'}
                    </Typography>
                  </Paper>

                  {/* Business Context and Categorization */}
                  <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Business Context
                      </Typography>
                      <Link href="#" sx={{ color: '#1976d2', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                        Occupation
                      </Link>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Categorization
                      </Typography>
                      <Link href="#" sx={{ color: '#1976d2', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                        Demographic Information
                      </Link>
                    </Grid>
                  </Grid>
                </>
              )}

              {leftSection === 'statistics' && (
                <ColumnStatistics statistics={statistics} loading={loadingStats} />
              )}

              {leftSection === 'details' && (
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                    Technical Details
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                        Asset Type
                      </Typography>
                      <Chip
                        label={element.assetType}
                        size="small"
                        color={getAssetTypeColor(element.assetType)}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                        Table
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {element.table || tableName || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                        Data Type
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'primary.main', fontWeight: 600 }}>
                        {element.dataType || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                        Nullable
                      </Typography>
                      <Chip
                        label={element.isNullable ? 'NULL' : 'NOT NULL'}
                        size="small"
                        color={element.isNullable ? 'warning' : 'success'}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                        Primary Key
                      </Typography>
                      <Chip
                        label={element.isPrimaryKey ? 'Yes' : 'No'}
                        size="small"
                        color={element.isPrimaryKey ? 'primary' : 'default'}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                        Foreign Key
                      </Typography>
                      <Chip
                        label={element.isForeignKey ? 'Yes' : 'No'}
                        size="small"
                        color={element.isForeignKey ? 'secondary' : 'default'}
                      />
                    </Grid>
                    {element.defaultValue && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                          Default Value
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
                          {element.defaultValue}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              )}

              {leftSection === 'comments' && (
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Comments
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    No comments yet.
                  </Typography>
                </Paper>
              )}
            </Box>
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <ColumnDiagram 
              columnName={element.name}
              tableName={tableName || 'CUSTOMER_DATA'}
              domain={domain || 'FINANCIALS'}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <ColumnTechnicalLineage 
              columnName={element.name}
              tableName={tableName || 'CUSTOMER_DATA'}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={3}>
            <ColumnQuality columnName={element.name} />
          </TabPanel>

          <TabPanel value={activeTab} index={4}>
            <ColumnResponsibilities 
              columnName={element.name}
              domain={domain || 'FINANCIALS'}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={5}>
            <ColumnHistory columnName={element.name} />
          </TabPanel>
        </Box>

        {/* Right Sidebar - At a Glance */}
        <Box
          sx={{
            width: 280,
            borderLeft: 1,
            borderColor: 'divider',
            bgcolor: '#fafafa',
            p: 2,
            overflow: 'auto'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              At a glance
            </Typography>
            <IconButton size="small">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Technical Data Type */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              Technical Data Type <InfoOutlinedIcon sx={{ fontSize: 12 }} />
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {element.dataType || 'VARCHAR'}
            </Typography>
          </Box>

          {/* Data Source Type */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              Data Source Type <InfoOutlinedIcon sx={{ fontSize: 12 }} />
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {tableName || 'Table'}
            </Typography>
          </Box>

          {/* Row Count */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              Row Count <InfoOutlinedIcon sx={{ fontSize: 12 }} />
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              100,000
            </Typography>
          </Box>

          {/* Page Visits */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              Page Visits <InfoOutlinedIcon sx={{ fontSize: 12 }} />
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              0
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Last modified */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Last modified
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </Typography>
          </Box>

          {/* Owner */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              Owner <InfoOutlinedIcon sx={{ fontSize: 12 }} />
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No assignments yet
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Data classification */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Data classification
            </Typography>
            <Chip
              label="Occupation"
              size="small"
              onDelete={() => {}}
              sx={{ bgcolor: 'divider' }}
            />
          </Box>

          {/* Articulation */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              Articulation <InfoOutlinedIcon sx={{ fontSize: 12 }} />
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                5%
              </Typography>
              <Box sx={{ flex: 1, height: 4, bgcolor: 'divider', borderRadius: 2 }}>
                <Box sx={{ width: '5%', height: '100%', bgcolor: '#4caf50', borderRadius: 2 }} />
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>

    {/* Edit Column Dialog */}
    <Dialog
      open={editDialogOpen}
      onClose={handleEditCancel}
      maxWidth="md"
      fullWidth
      aria-labelledby="edit-column-dialog-title"
    >
      <DialogTitle
        id="edit-column-dialog-title"
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          fontFamily: 'Merriweather, serif',
          fontWeight: 700,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <span>Edit Column: {element?.name}</span>
        <IconButton
          onClick={handleEditCancel}
          sx={{ color: 'white' }}
          aria-label="Close edit dialog"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ mt: 3 }}>
        <Grid container spacing={3}>
          {/* Description */}
          <Grid item xs={12}>
            <TextField
              label="Description"
              multiline
              rows={4}
              fullWidth
              value={editForm.description}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              placeholder="Enter column description"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main'
                  }
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: 'primary.main'
                }
              }}
            />
          </Grid>

          {/* Data Type */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Data Type"
              fullWidth
              value={editForm.dataType}
              onChange={(e) => setEditForm({ ...editForm, dataType: e.target.value })}
              placeholder="e.g., VARCHAR(100)"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main'
                  }
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: 'primary.main'
                }
              }}
            />
          </Grid>

          {/* Nullable */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="nullable-label">Nullable</InputLabel>
              <Select
                labelId="nullable-label"
                value={editForm.isNullable ? 'true' : 'false'}
                label="Nullable"
                onChange={(e) => setEditForm({ ...editForm, isNullable: e.target.value === 'true' })}
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main'
                  }
                }}
              >
                <MenuItem value="false">NOT NULL</MenuItem>
                <MenuItem value="true">NULL</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={handleEditCancel}
          variant="outlined"
          sx={{
            color: 'primary.main',
            borderColor: 'primary.main',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'rgba(0, 51, 102, 0.04)'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleEditSave}
          variant="contained"
          disabled={saving}
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              bgcolor: '#005a9c'
            }
          }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Notification Snackbar */}
    <Snackbar
      open={notification.open}
      autoHideDuration={6000}
      onClose={() => setNotification({ ...notification, open: false })}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        onClose={() => setNotification({ ...notification, open: false })}
        severity={notification.severity as 'success' | 'error' | 'info' | 'warning'}
        sx={{ width: '100%' }}
      >
        {notification.message}
      </Alert>
    </Snackbar>
    </>
  );
};

export default ColumnDetailDialog;
